<template>
    <div class="app-shell">
        <header class="app-header">
            <div class="header-left">
                <div class="logo">
                    <i class="pi pi-megaphone"></i>
                </div>
                <div class="app-title">
                    <h2>{{ appSettingsStore.appTitle }}</h2>
                    <span class="version">v{{ APP_VERSION }}</span>
                </div>
                <div v-if="pageHeader.showBackButton" class="header-separator"></div>
                <Button
                    v-if="pageHeader.showBackButton"
                    icon="pi pi-arrow-left"
                    label="Back"
                    text
                    @click="pageHeader.onBack"
                    class="header-back-button"
                />
                <div v-if="pageHeader.title" class="page-header-title">
                    <h2>{{ pageHeader.title }}</h2>
                </div>
            </div>
            <div class="header-right">
                <div class="user-info">
                    <div class="user-avatar">
                        <i class="pi pi-user"></i>
                    </div>
                    <div class="user-details">
                        <span class="user-email">{{ userEmail || (authStore.loadingUser ? "Loading..." : "") }}</span>
                        <span class="user-role" :class="{ 'role-admin': authStore.isAdmin }">
                            {{ authStore.isAdmin ? "Admin" : "User" }}
                        </span>
                    </div>
                </div>
                <Button label="Logout" icon="pi pi-sign-out" severity="secondary" outlined @click="handleLogout" />
            </div>
        </header>
        <div class="app-body">
            <aside class="app-sidebar">
                <nav class="sidebar-nav">
                    <router-link
                        v-for="item in flatMenuItems"
                        :key="item.path"
                        :to="item.path"
                        class="nav-item"
                        :class="{ active: $route.path === item.path }"
                    >
                        <i :class="item.icon"></i>
                        <span>{{ item.label }}</span>
                    </router-link>

                    <!-- Settings group -->
                    <div class="nav-group">
                        <button
                            class="nav-item nav-group-toggle"
                            :class="{ active: isSettingsActive }"
                            @click="settingsOpen = !settingsOpen"
                        >
                            <i class="pi pi-cog"></i>
                            <span>Settings</span>
                            <i class="pi toggle-icon" :class="settingsOpen ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                        </button>
                        <div v-if="settingsOpen" class="nav-children">
                            <router-link
                                v-for="child in settingsItems"
                                :key="child.path"
                                :to="child.path"
                                class="nav-item nav-child"
                                :class="{ active: $route.path === child.path }"
                            >
                                <i :class="child.icon"></i>
                                <span>{{ child.label }}</span>
                            </router-link>
                        </div>
                    </div>
                </nav>
            </aside>
            <main class="app-main">
                <router-view />
            </main>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, reactive, provide } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "primevue/usetoast";
import Button from "primevue/button";
import { useAuthStore } from "@/store/authStore";
import { useAppSettingsStore } from "@/store/appSettingsStore";
import { APP_VERSION } from "@/config/version";

const router = useRouter();
const route = useRoute();
const toast = useToast();
const authStore = useAuthStore();
const appSettingsStore = useAppSettingsStore();

const userEmail = computed(() => authStore.user?.email || "");

const pageHeader = reactive({
    title: "",
    subtitle: "",
    showBackButton: false,
    onBack: () => {},
});

provide("pageHeader", pageHeader);

onMounted(async () => {
    if (authStore.token && !authStore.user && !authStore.loadingUser) {
        await authStore.fetchMe();
    }
});

const flatMenuItems = ref([
    { path: "/", label: "Dashboard", icon: "pi pi-home" },
    { path: "/meta-campaigns", label: "Meta Campaigns", icon: "pi pi-facebook" },
    { path: "/meta-campaigns/logs", label: "Rule Execution Logs", icon: "pi pi-list" },
]);

const settingsItems = ref([
    { path: "/settings/users", label: "Users", icon: "pi pi-users" },
    { path: "/settings", label: "Main Settings", icon: "pi pi-sliders-h" },
]);

const settingsOpen = ref(false);

const isSettingsActive = computed(() =>
    route.path.startsWith("/settings")
);

// Auto-expand settings group when on a settings page
watch(
    () => route.path,
    (path) => {
        if (path.startsWith("/settings")) {
            settingsOpen.value = true;
        }
    },
    { immediate: true }
);

async function handleLogout() {
    authStore.logout();
    toast.add({
        severity: "info",
        summary: "Logged out",
        detail: "You have been logged out",
        life: 3000,
    });
    await router.push({ name: "login" });
}
</script>

<style scoped>
.app-shell {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.app-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: white;
    border-bottom: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.logo {
    width: 40px;
    height: 40px;
    background: var(--brand-gradient, linear-gradient(135deg, #0099ff 0%, #0064e0 100%));
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.25rem;
}

.app-title h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.version {
    font-size: 0.75rem;
    color: #6b7280;
    margin-left: 0.5rem;
}

.header-separator {
    width: 1px;
    height: 32px;
    background-color: #e5e7eb;
    margin: 0 1.5rem;
    flex-shrink: 0;
}

.header-back-button {
    flex-shrink: 0;
}

.page-header-title h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    line-height: 1.2;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.user-avatar {
    width: 36px;
    height: 36px;
    background: #f3f4f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
}

.user-details {
    display: flex;
    flex-direction: column;
}

.user-email {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1f2937;
}

.user-role {
    font-size: 0.75rem;
    color: #6b7280;
}

.role-admin {
    color: var(--brand-primary, #0099ff);
    font-weight: 600;
}

.app-body {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.app-sidebar {
    width: 260px;
    background: white;
    border-right: 1px solid #e5e7eb;
    overflow-y: auto;
    flex-shrink: 0;
}

.sidebar-nav {
    padding: 1rem 0;
}

.nav-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
    color: #4b5563;
    text-decoration: none;
    transition: all 0.2s;
    width: 100%;
}

.nav-item:hover {
    background: #f9fafb;
    color: #1f2937;
}

.nav-item.active {
    background: var(--brand-gradient, linear-gradient(135deg, #0099ff 0%, #0064e0 100%));
    color: white;
}

.nav-item i {
    font-size: 1.125rem;
}

/* Settings group */
.nav-group-toggle {
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 0.9rem;
}

.nav-group-toggle .toggle-icon {
    margin-left: auto;
    font-size: 0.75rem;
    opacity: 0.6;
}

.nav-children {
    border-left: 2px solid #f3f4f6;
    margin-left: 1.5rem;
}

.nav-child {
    padding: 0.6rem 1rem 0.6rem 1.25rem;
    font-size: 0.875rem;
}

.nav-child i {
    font-size: 1rem;
}

.app-main {
    flex: 1;
    overflow-y: auto;
    background: #f9fafb;
    padding: 2rem;
}
</style>
